import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Slide,
  TableCell,
  TextField,
  Theme,
  Typography,
  styled,
} from '@mui/material';
import HorizontalRuleOutlinedIcon from '@mui/icons-material/HorizontalRuleOutlined';
import {
  HandleHideDummyProps,
  MemberRatesUpdateForm,
  UpdateDummyMemberProps,
} from './types';
import { StyledTableRow } from './StyledTableRow';
import CustomSelect from './components/CustomSelect';
import { CustomBudgetBox, CustomBudgetCell } from './components';
import { MarginHighlightCell } from './components/MarginHighlightCell';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import PersonRemoveOutlined from '@mui/icons-material/PersonRemoveOutlined';
import { useFormik } from 'formik';
import { useTheme } from '@mui/material/styles';
import { compensationMarginsValidationSchema } from './CompensationMargins.schema';
import {
  fetchCompensationRange,
  removeDummyMember,
  toggleHideDummyMember,
  updateDummyMemberField,
} from '../../redux/account';
import { useAppDispatch } from '../../redux/hooks';
import { DummyMember, LegalLocationType, MarginHighlight } from '../../types';
import {
  legalLocations,
  streams,
  getSeniorities,
} from './components/CustomSelect.const';
import { NumericFormat } from 'react-number-format';
import { RateField } from './components/RateField';
import {
  MARGINS_NAME_COLUMN_WIDTH,
  marginsActionsColumnBodySx,
  marginsPayRangeColumnSx,
} from './marginsTableLayout';

const CustomValueSelect = styled((props: any) => (
  <CustomSelect {...props}></CustomSelect>
))({
  '.MuiFilledInput-root': {
    width: '100%',
    boxSizing: 'border-box',
  },
  '.MuiFilledInput-input': {
    minWidth: '0',
    boxSizing: 'border-box',
  },
});

export interface CompensationDummyMemberRowProps {
  id: string;
  current?: Element | null;
  isLastRow: boolean;
  dummyMember: DummyMember;
  grossMarginHighlights: MarginHighlight[];
  netMarginHighlights: MarginHighlight[];
  projectId: string;
}

export const CompensationDummyMemberRow = ({
  id: memberId,
  isLastRow,
  grossMarginHighlights,
  netMarginHighlights,
  current,
  dummyMember,
  projectId,
}: CompensationDummyMemberRowProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<null | HTMLElement>(null);

  const closeActionsMenu = useCallback(() => {
    setActionsMenuAnchor(null);
  }, []);

  const formik = useFormik<MemberRatesUpdateForm>({
    initialValues: {
      compensationRate: 0,
      billRate: dummyMember.billRate ?? 20,
      allocation: dummyMember.allocation ?? 100,
      memberId,
      isHidden: dummyMember.isHidden ?? false,
    },
    validationSchema: compensationMarginsValidationSchema,
    onSubmit: () => {
      return;
    },
  });

  const handleHideDummyMember = useCallback(
    (data: HandleHideDummyProps) => {
      const { id, isHidden } = data;

      dispatch(
        toggleHideDummyMember({
          id,
          isHidden,
        }),
      );
    },
    [dispatch],
  );

  const handleRemoveDummyMember = useCallback(
    (data: HandleHideDummyProps) => {
      handleHideDummyMember(data);
      dispatch(removeDummyMember(data));
    },
    [dispatch, handleHideDummyMember],
  );

  const handleUpdateDummyMembers = useCallback(
    (data: UpdateDummyMemberProps) => {
      dispatch(
        updateDummyMemberField({
          id: data.memberId,
          fieldName: data.fieldName,
          value: data.value,
          projectId: data.projectId,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    formik.setFieldValue('billRate', dummyMember.billRate);
    formik.setFieldValue('compensationRate', dummyMember.compensationRate);
    // eslint-disable-next-line
  }, [dummyMember.billRate, dummyMember.compensationRate]);

  useEffect(() => {
    if (dummyMember.country && dummyMember.stream && dummyMember.seniority) {
      dispatch(
        fetchCompensationRange({
          memberId: memberId,
          legalLocation: dummyMember.country,
          seniority: dummyMember.seniority,
          stream: dummyMember.stream,
          employmentType: dummyMember.employmentType,
        }),
      );
    }
  }, [
    dispatch,
    dummyMember.country,
    dummyMember.stream,
    memberId,
    dummyMember.seniority,
    dummyMember.employmentType,
  ]);

  const onRateChange = useCallback(
    (data: { fieldName: string; value?: number }) => {
      handleUpdateDummyMembers({
        memberId: memberId,
        fieldName: data.fieldName,
        value: data.value,
        projectId,
      });
      formik.setFieldValue(data.fieldName, data.value);
    },
    [handleUpdateDummyMembers, formik, memberId, projectId],
  );

  const onHandleSelect = useCallback(
    (data: { name: string; value: string | number }) => {
      handleUpdateDummyMembers({
        memberId: memberId,
        fieldName: data.name,
        value: data.value,
        projectId,
      });
    },
    [handleUpdateDummyMembers, memberId, projectId],
  );

  const onHandleSelectStream = useCallback(
    (data: { name: string; value: string | number }) => {
      handleUpdateDummyMembers({
        memberId: memberId,
        fieldName: 'stream',
        value: data.value,
        projectId,
      });
      handleUpdateDummyMembers({
        memberId: memberId,
        fieldName: 'seniority',
        value: undefined,
        projectId,
      });
    },
    [handleUpdateDummyMembers, memberId, projectId],
  );
  const onHandleSelectLocation = useCallback(
    (data: { name: string; value: string | number }) => {
      const location = legalLocations.find((x) => x.id === data.value);
      handleUpdateDummyMembers({
        memberId: memberId,
        fieldName: 'country',
        value: location?.country,
        projectId,
      });
      handleUpdateDummyMembers({
        memberId: memberId,
        fieldName: 'employmentType',
        value: location?.employmentType,
        projectId,
      });
    },
    [handleUpdateDummyMembers, memberId, projectId],
  );

  const handleAllocationChange = useCallback(
    (value: string | undefined) => {
      formik.setFieldValue('allocation', Number(value));
      handleUpdateDummyMembers({
        memberId: memberId,
        fieldName: 'allocation',
        value: Number(value),
        projectId,
      });
    },
    [formik, handleUpdateDummyMembers, memberId, projectId],
  );

  const selectedLocation = useMemo(
    () =>
      legalLocations.find(
        (x) =>
          x.country === dummyMember.country &&
          x.employmentType === dummyMember.employmentType,
      ),
    [dummyMember.country, dummyMember.employmentType],
  );

  const seniorities = useMemo(
    () => getSeniorities(dummyMember.stream),
    [dummyMember.stream],
  );

  const columnWidth = (theme: Theme) => theme.spacing(10);

  return (
    <Slide direction="down" in container={current}>
      <StyledTableRow isLastRow={isLastRow} isHidden={dummyMember.isHidden}>
        <TableCell
          size="small"
          variant="body"
          sx={{
            width: MARGINS_NAME_COLUMN_WIDTH,
            minWidth: MARGINS_NAME_COLUMN_WIDTH,
            maxWidth: MARGINS_NAME_COLUMN_WIDTH,
            textOverflow: 'ellipsis',
            borderStyle: 'border-box',
            borderBottom: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <CustomValueSelect
              dense
              onChange={onHandleSelectLocation}
              minWidth={82}
              label="Country"
              options={legalLocations}
              value={selectedLocation?.id ?? ''}
              renderValue={(selectedId: string) =>
                legalLocations.find((l) => l.id === selectedId)?.countryCode ??
                ''
              }
              renderOption={(option: LegalLocationType, index: number) => (
                <MenuItem key={index} value={option.id}>
                  {option.country}
                  {option.employmentType && (
                    <> &bull; {option.employmentType}</>
                  )}
                </MenuItem>
              )}
            />
            <CustomValueSelect
              dense
              onChange={onHandleSelectStream}
              minWidth={88}
              label="Role"
              options={streams}
              value={dummyMember.stream ?? ''}
              renderOption={(option: string, index: number) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              )}
            />
            <CustomValueSelect
              dense
              minWidth={88}
              label="Seniority"
              options={seniorities}
              onChange={onHandleSelect}
              value={dummyMember.seniority ?? ''}
              renderOption={(option: string, index: number) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              )}
            />
          </Box>
        </TableCell>
        <MarginHighlightCell
          align="center"
          sx={{ borderBottom: 0, width: columnWidth }}
          marginHighlights={grossMarginHighlights || []}
          value={dummyMember.grossMargin}
          marginCategory="Gross Margin"
        >
          {dummyMember.grossMargin == null ? (
            <HorizontalRuleOutlinedIcon color="disabled" />
          ) : (
            `${dummyMember.grossMargin}%`
          )}
        </MarginHighlightCell>

        <MarginHighlightCell
          align="center"
          sx={{ borderBottom: 0, width: columnWidth }}
          marginHighlights={netMarginHighlights || []}
          value={dummyMember.netMargin}
          marginCategory="Net Margin"
        >
          {dummyMember.netMargin == null ? (
            <HorizontalRuleOutlinedIcon color="disabled" />
          ) : (
            `${dummyMember.netMargin}%`
          )}
        </MarginHighlightCell>
        <TableCell align="center" sx={{ borderBottom: 0, width: columnWidth }}>
          <RateField
            id={'compensationRate'}
            name={'compensationRate'}
            value={formik.values.compensationRate}
            handleChange={onRateChange}
            error={Boolean(formik.errors.billRate)}
            //eslint-disable-next-line
            autoFocus
          />
        </TableCell>
        <CustomBudgetCell
          sx={{
            borderBottom: 0,
            ...marginsPayRangeColumnSx(theme),
          }}
          align="center"
          justifyContent="center"
        >
          {dummyMember.payRange ? (
            <>
              <CustomBudgetBox>$</CustomBudgetBox>
              {'\u2009'}
              {dummyMember.payRange[0]}
              {'\u2009'}
              <CustomBudgetBox>..</CustomBudgetBox>
              {'\u2009'}
              <CustomBudgetBox>$</CustomBudgetBox>
              {'\u2009'}
              {dummyMember.payRange[1]}
            </>
          ) : (
            <HorizontalRuleOutlinedIcon color="disabled" />
          )}
        </CustomBudgetCell>
        <TableCell align="center" sx={{ borderBottom: 0, width: columnWidth }}>
          <RateField
            id={'billRate'}
            name={'billRate'}
            value={formik.values.billRate}
            handleChange={onRateChange}
            error={Boolean(formik.errors.billRate)}
          />
        </TableCell>
        <CustomBudgetCell
          sx={{ borderBottom: 0, width: columnWidth }}
          align="center"
          justifyContent="center"
        >
          {dummyMember.rateCard ? (
            <>
              <CustomBudgetBox>$</CustomBudgetBox>
              {'\u2009'}
              {dummyMember.rateCard}
            </>
          ) : (
            <HorizontalRuleOutlinedIcon color="disabled" />
          )}
        </CustomBudgetCell>

        <TableCell align="center" sx={{ borderBottom: 0, width: columnWidth }}>
          <NumericFormat
            customInput={TextField}
            id="allocation"
            autoComplete="off"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              size: 'small',
              sx: {
                maxWidth: (theme: Theme) => theme.spacing(8.5),
                minWidth: (theme: Theme) => theme.spacing(7.5),
                maxHeight: (theme: Theme) => theme.spacing(4.5),
              },
            }}
            inputProps={{ maxLength: 3 }}
            name="allocation"
            inputMode="decimal"
            InputLabelProps={{
              shrink: true,
            }}
            onValueChange={({ value }) => handleAllocationChange(value)}
            value={formik.values.allocation}
            variant="filled"
            helperText={formik.errors.allocation}
            hiddenLabel
          />
        </TableCell>

        <TableCell
          align="center"
          sx={(theme: Theme) => marginsActionsColumnBodySx(theme)}
        >
          <IconButton
            aria-label="Open actions"
            aria-controls={
              actionsMenuAnchor ? 'dummy-member-actions' : undefined
            }
            aria-expanded={Boolean(actionsMenuAnchor)}
            aria-haspopup="menu"
            onClick={(event) => setActionsMenuAnchor(event.currentTarget)}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="dummy-member-actions"
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={closeActionsMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 0.5,
                minWidth: 200,
                borderRadius: 1,
                boxShadow: 3,
              },
            }}
          >
            <Typography
              component="div"
              variant="caption"
              sx={{
                px: 2,
                pt: 1,
                pb: 0.5,
                display: 'block',
                color: 'text.secondary',
              }}
            >
              Actions
            </Typography>
            <MenuItem
              onClick={() => {
                handleHideDummyMember({
                  id: dummyMember.id,
                  compensationRate: formik.values.compensationRate,
                  billRate: formik.values.billRate,
                  allocation: formik.values.allocation,
                  isHidden: !dummyMember.isHidden,
                  payRange: formik.values.payRange,
                });
                closeActionsMenu();
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {dummyMember.isHidden ? (
                  <PersonAddOutlinedIcon fontSize="small" />
                ) : (
                  <PersonOffOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={dummyMember.isHidden ? 'Activate' : 'Deactivate'}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleRemoveDummyMember({
                  id: dummyMember.id,
                  compensationRate: formik.values.compensationRate,
                  billRate: formik.values.billRate,
                  allocation: formik.values.allocation,
                  isHidden: !dummyMember.isHidden,
                  payRange: formik.values.payRange,
                  projectId,
                });
                closeActionsMenu();
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <PersonRemoveOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Remove" />
            </MenuItem>
          </Menu>
        </TableCell>
      </StyledTableRow>
    </Slide>
  );
};
